"use client";

import { IconChevronDownMedium } from "@central-icons-react/round-outlined-radius-3-stroke-2";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuScrollSection,
} from "@/components/ui/popover-menu";
import { cn } from "@/lib/utils";

const selectTriggerVariants = cva(
  "inline-flex items-center gap-0.5 tracking-[-0.01em] text-text-primary outline-none transition-colors hover:bg-background-tertiary focus-visible:ring-2 focus-visible:ring-neutral-06/30 disabled:pointer-events-none disabled:opacity-60",
  {
    variants: {
      variant: {
        chip: "rounded-md bg-background-secondary px-1.5 py-0.5 text-[14px]",
        field:
          "h-10 w-full justify-between rounded-lg bg-background-secondary px-3 text-left text-[14px]",
      },
    },
    defaultVariants: {
      variant: "chip",
    },
  },
);

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
};

export interface SelectProps<T extends string> extends VariantProps<
  typeof selectTriggerVariants
> {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (next: T) => void;
  ariaLabel: string;
  disabled?: boolean;
  /** Shown when `value` matches no option; also greys the trigger label. */
  placeholder?: string;
  triggerClassName?: string;
  rowClassName?: string;
  menuContentClassName?: string;
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
  placeholder,
  variant,
  triggerClassName,
  rowClassName,
  menuContentClassName,
}: SelectProps<T>) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((option) => option.value === value);
  const label = selected?.label ?? placeholder ?? value;

  const select = (next: T) => {
    if (next !== value) onChange(next);
    setOpen(false);
  };

  return (
    <PopoverMenu
      open={open}
      onOpenChange={disabled ? undefined : setOpen}
      rowClassName={rowClassName}
      menuContentClassName={menuContentClassName}
      trigger={
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={
            // A full custom trigger class replaces the variant styling
            // (matching the retired TriggerFieldSelect); otherwise use the
            // token-based chip/field trigger.
            triggerClassName ??
            cn(
              selectTriggerVariants({ variant }),
              !selected && "text-neutral-07",
            )
          }
        >
          <span className="truncate">{label}</span>
          <IconChevronDownMedium
            className={cn(
              "size-3 shrink-0 text-neutral-07 transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      }
      menu={
        <PopoverMenuScrollSection className="flex gap-0">
          {options.map((option) => (
            <PopoverMenuItem
              key={option.value}
              label={option.label}
              selected={option.value === value}
              disabled={disabled}
              onClick={() => select(option.value)}
            />
          ))}
        </PopoverMenuScrollSection>
      }
    />
  );
}

export { selectTriggerVariants };
